/**
 * AI-assisted dataset semantics with a LIVE DEBUGGER (ADR-0026, L2).
 *
 * The per-dataset counterpart to `ProposeAiRelationshipsDialog`: instead of a
 * one-shot "fill the form" call, the operator watches Odino draft the descriptor
 * in real time — the context assembled (schema + a sample of rows), the model's
 * thinking + raw JSON as it streams, and the parsed proposal. Nothing is applied
 * until the operator clicks **Applica bozza** (the page then merges it into the
 * still-unsaved form for review).
 *
 * The stream is SSE-over-POST (`lib/datasetSemanticsAi.streamProposeSemantics`) —
 * read by hand, not via EventSource (which can't POST + carry the bearer token).
 */
import { Bug, Loader2, Sparkles } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import type { DatasetSemanticsInput } from "@/lib/api-dataset-semantics";
import { streamProposeSemantics, type SemanticsAiEvent } from "@/lib/datasetSemanticsAi";

/** One line in the debugger console. */
interface LogLine {
  tone: "step" | "context" | "usage" | "error";
  text: string;
}

export function ProposeSemanticsDialog({
  open,
  onOpenChange,
  datasetId,
  onApply,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  datasetId: string;
  /** Hand the parsed draft back to the page, which merges it into the form. */
  onApply: (draft: DatasetSemanticsInput) => void;
}) {
  const { t } = useTranslation();

  const [thinking, setThinking] = React.useState(false);
  const [running, setRunning] = React.useState(false);
  const [finished, setFinished] = React.useState(false);
  const [log, setLog] = React.useState<LogLine[]>([]);
  const [output, setOutput] = React.useState("");
  const [thoughts, setThoughts] = React.useState("");
  const [proposal, setProposal] = React.useState<DatasetSemanticsInput | null>(null);
  const abortRef = React.useRef<AbortController | null>(null);
  const consoleRef = React.useRef<HTMLDivElement>(null);
  // Did a TERMINAL event (proposal / error / done) arrive? If the stream just ends
  // without one (a mid-stream disconnect on a long, wide-table generation), the UI
  // would otherwise go silently idle — we surface an explicit error instead.
  const sawTerminalRef = React.useRef(false);

  // Reset everything when the dialog (re)opens.
  React.useEffect(() => {
    if (open) {
      setThinking(false);
      reset();
    }
    return () => abortRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Keep the console pinned to the latest output (scrollTop, not scrollTo — the
  // latter is unimplemented in jsdom and would throw in tests).
  React.useEffect(() => {
    if (consoleRef.current) consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
  }, [output, thoughts, log]);

  function reset() {
    setRunning(false);
    setFinished(false);
    setLog([]);
    setOutput("");
    setThoughts("");
    setProposal(null);
    sawTerminalRef.current = false;
  }

  function onEvent(ev: SemanticsAiEvent) {
    switch (ev.type) {
      case "step":
        setLog((l) => [...l, { tone: "step", text: ev.label }]);
        break;
      case "context":
        setLog((l) => [
          ...l,
          {
            tone: "context",
            text: t("semantics.proposeAi.contextLine", {
              defaultValue: "{{fields}} campi · {{rows}} righe di esempio",
              fields: ev.field_count,
              rows: ev.row_count,
            }),
          },
        ]);
        break;
      case "thinking":
        setThoughts((s) => s + ev.text);
        break;
      case "delta":
        setOutput((s) => s + ev.text);
        break;
      case "proposal":
        sawTerminalRef.current = true;
        setProposal(ev.proposal);
        if (ev.usage?.output_tokens != null) {
          setLog((l) => [
            ...l,
            {
              tone: "usage",
              text: t("semantics.proposeAi.usageLine", {
                defaultValue: "{{out}} token generati ({{in}} in input)",
                out: ev.usage.output_tokens,
                in: ev.usage.input_tokens ?? 0,
              }),
            },
          ]);
        }
        break;
      case "error":
        sawTerminalRef.current = true;
        setLog((l) => [...l, { tone: "error", text: ev.message }]);
        break;
      case "done":
        sawTerminalRef.current = true;
        setRunning(false);
        setFinished(true);
        break;
    }
  }

  async function start() {
    reset();
    setRunning(true);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      await streamProposeSemantics(datasetId, { thinking }, onEvent, ctrl.signal);
      // The stream closed without a terminal event → it was interrupted (timeout /
      // disconnect). Never leave the user staring at a frozen, blank console.
      if (!sawTerminalRef.current && !ctrl.signal.aborted) {
        onEvent({
          type: "error",
          message: t(
            "semantics.proposeAi.interrupted",
            "Proposta interrotta prima del completamento (la tabella è molto ampia). Riprova.",
          ),
        });
      }
    } catch {
      if (!ctrl.signal.aborted) onEvent({ type: "error", message: "stream_failed" });
    } finally {
      setRunning(false);
      setFinished(true);
    }
  }

  function apply() {
    if (!proposal) return;
    onApply(proposal);
    onOpenChange(false);
  }

  const fieldCount = proposal ? Object.keys(proposal.fields).length : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm font-semibold">
            <Sparkles className="h-4 w-4" strokeWidth={1.5} />
            {t("semantics.proposeAi.title", "Proponi semantica con AI")}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p className="mb-3 text-xs text-muted-foreground">
            {t(
              "semantics.proposeAi.body",
              "Odino introspeziona i campi e un campione di righe e propone label, descrizioni, tipi semantici e sensibilità. Niente viene salvato: rivedi la bozza prima di applicarla. Segui il ragionamento in tempo reale.",
            )}
          </p>

          {/* Controls */}
          <div className="mb-3 flex flex-wrap items-end gap-3">
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <Switch checked={thinking} onCheckedChange={setThinking} disabled={running} />
              {t("semantics.proposeAi.thinking", "Ragionamento esteso")}
            </label>
            <Button size="sm" onClick={start} disabled={running}>
              {running ? (
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
              ) : (
                <Sparkles className="h-4 w-4" strokeWidth={1.5} />
              )}
              {running
                ? t("semantics.proposeAi.running", "Analisi…")
                : t("semantics.proposeAi.start", "Avvia")}
            </Button>
          </div>

          {/* Debugger console */}
          {(running || finished) && (
            <div
              ref={consoleRef}
              className="mb-3 max-h-56 overflow-auto rounded-lg border border-border/60 bg-surface/60 p-3 font-mono text-[11px] leading-relaxed"
            >
              <div className="mb-1 flex items-center gap-1.5 text-muted-foreground">
                <Bug className="h-3.5 w-3.5" strokeWidth={1.5} />
                {t("semantics.proposeAi.debugger", "Debugger")}
              </div>
              {log.map((line, i) => (
                <div
                  key={i}
                  className={
                    line.tone === "error"
                      ? "text-danger"
                      : line.tone === "step"
                        ? "text-foreground"
                        : "text-muted-foreground"
                  }
                >
                  {line.tone === "step" ? "▸ " : "  "}
                  {line.text}
                </div>
              ))}
              {thoughts && (
                <div className="mt-1 whitespace-pre-wrap italic text-muted-foreground">{thoughts}</div>
              )}
              {output && <div className="mt-1 whitespace-pre-wrap text-muted-foreground/80">{output}</div>}
              {running && <span className="inline-block animate-pulse text-accent">▍</span>}
            </div>
          )}

          {/* Parsed proposal preview */}
          {proposal && (
            <div className="rounded-lg border border-border/60 p-3">
              <div className="mb-2 flex items-center gap-2">
                <span className="text-sm font-medium">{proposal.label || t("semantics.proposeAi.noLabel", "(senza label)")}</span>
                {proposal.domain && (
                  <Badge tone="neutral" className="text-[10px]">
                    {proposal.domain}
                  </Badge>
                )}
                <Badge tone="active" className="text-[10px]">
                  {t(`semantics.sensitivity.${proposal.sensitivity}`, proposal.sensitivity)}
                </Badge>
                <span className="ml-auto text-[11px] text-muted-foreground">
                  {t("semantics.proposeAi.fieldsDescribed", "{{n}} campi descritti", {
                    n: fieldCount,
                  })}
                </span>
              </div>
              {proposal.description && (
                <p className="text-xs text-muted-foreground">{proposal.description}</p>
              )}
            </div>
          )}

          {finished && !proposal && (
            <p className="py-6 text-center text-xs text-muted-foreground">
              {t("semantics.proposeAi.none", "Nessuna bozza generata. Riprova.")}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            {t("common.cancel", "Annulla")}
          </Button>
          <Button size="sm" disabled={!proposal || running} onClick={apply}>
            {t("semantics.proposeAi.apply", "Applica bozza")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
